import { useFormikContext } from 'formik';
import { Lock } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { SimpleSelect } from '@/components/ui/simple-select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FormError } from '@/components/ui/form-field';

import { usePropertyDetailMode } from '../hooks/usePropertyDetailMode';

import type { Option } from '@/components/ui/multi-select';

type FieldType = 'text' | 'number' | 'date' | 'select' | 'combobox' | 'radio';

export interface DetailFieldProps {
  label: string;
  name: string;
  type: FieldType;
  value: string | number | null | undefined;
  options?: Option[];
  required?: boolean;
  locked?: boolean;
  placeholder?: string;
  maxLength?: number;
  step?: number;
}

const formatViewValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

export function DetailField({
  label,
  name,
  type,
  value,
  options = [],
  required,
  locked,
  placeholder,
  maxLength,
  step,
}: DetailFieldProps) {
  const { mode, isSyncManaged } = usePropertyDetailMode();
  const effectiveLocked = locked || isSyncManaged;
  const isView = mode === 'view' || effectiveLocked;

  if (isView) {
    return (
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
          {label}
          {effectiveLocked && (
            <span data-testid="detail-field-lock" aria-hidden="true">
              <Lock className="h-3.5 w-3.5" />
            </span>
          )}
        </Label>
        <div className="text-foreground mt-0.5">{formatViewValue(value)}</div>
      </div>
    );
  }

  return (
    <DetailFieldInput
      label={label}
      name={name}
      type={type}
      options={options}
      required={required}
      placeholder={placeholder}
      maxLength={maxLength}
      step={step}
    />
  );
}

interface DetailFieldInputProps {
  label: string;
  name: string;
  type: FieldType;
  options: Option[];
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  step?: number;
}

function DetailFieldInput({
  label,
  name,
  type,
  options,
  required,
  placeholder,
  maxLength,
  step,
}: DetailFieldInputProps) {
  const formik = useFormikContext<Record<string, string | number>>();
  const { values, errors, touched, handleChange, setFieldValue } = formik;
  const error = touched[name] ? (errors[name] as string | undefined) : undefined;
  const errorCls = error ? 'border-destructive' : '';

  switch (type) {
    case 'text':
      return (
        <div>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <Input
            id={name}
            name={name}
            type="text"
            value={(values[name] as string) ?? ''}
            onChange={handleChange}
            maxLength={maxLength}
            placeholder={placeholder}
            className={errorCls}
            data-testid={`${name}_id`}
          />
          <FormError message={error} />
        </div>
      );
    case 'date':
      return (
        <div>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <Input
            id={name}
            name={name}
            type="date"
            value={(values[name] as string) ?? ''}
            onChange={handleChange}
            className={errorCls}
            data-testid={`${name}_id`}
          />
          <FormError message={error} />
        </div>
      );
    case 'number':
      return (
        <div>
          <Label htmlFor={name} required={required}>
            {label}
          </Label>
          <NumberInput
            id={name}
            name={name}
            value={values[name] as number}
            step={step}
            onChange={(val) => setFieldValue(name, val)}
            data-testid={`${name}_id`}
          />
          <FormError message={error} />
        </div>
      );
    case 'select':
      return (
        <SimpleSelect
          options={options}
          name={name}
          controlId={name}
          ariaDescribedBy={`${name}-select`}
          label={label}
          isRequired={required}
          value={options.find((o) => o.value === values[name]) ?? null}
          setFieldValue={setFieldValue}
          errors={error}
          field={name}
          placeholder={placeholder}
        />
      );
    case 'combobox':
      return (
        <Combobox
          options={options}
          md="12"
          controlId={name}
          label={label}
          isRequired={required}
          ariaDescribedBy={`${name}-select`}
          placeholder={placeholder}
          name={name}
          searchPlaceholder={`Search ${label.toLowerCase()}...`}
          value={options.find((o) => o.value === values[name])}
          errors={error}
          field={name}
          setFieldValue={setFieldValue}
        />
      );
    case 'radio':
      return (
        <div>
          <Label>{label}</Label>
          <RadioGroup
            value={values[name] as string}
            onValueChange={(v) => setFieldValue(name, v)}
            name={name}
            className={cn('flex gap-4', errorCls)}
          >
            {options.map((o) => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem value={String(o.value)} id={`${name}-${o.value}`} />
                <Label
                  htmlFor={`${name}-${o.value}`}
                  className="mb-0 font-normal"
                >
                  {o.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <FormError message={error} />
        </div>
      );
    default:
      return null;
  }
}
